/**
 * Onboarding / profile form model (docs/GAME_DESIGN.md § 8): four short steps
 * — nickname → grade → school (country → province → city → school) →
 * leaderboard opt-in. Pure: the screen keeps a ProfileDraft in state and asks
 * this module what blocks each step and what to send to the backend.
 *
 * Every i18n key is written out literally so test/i18n.test.ts can find it.
 */
import { LEVEL_THEMES, type Locale } from '@dropstack/core';
import type { PlayerProfile, ProfileInput } from '../../contracts.ts';
import type { ProfileFieldProblem } from '../../services/backend/api-types.ts';
import { HOME_COUNTRY, findSaProvince, provincesFor } from '../../services/geo.ts';
import { isNicknameAllowed, type NicknameProblem } from '../../services/nicknames.ts';
import { placeTextProblem, validateProfileInput, type PlaceTextProblem } from '../../services/profileValidation.ts';
import { collapseSpaces } from '../../services/text.ts';

export type OnboardingStepId = 'name' | 'grade' | 'school' | 'leaderboard';

export const ONBOARDING_STEPS: readonly OnboardingStepId[] = ['name', 'grade', 'school', 'leaderboard'];

export const GRADES: readonly number[] = Array.from({ length: 12 }, (_, i) => i + 1);

/** What the form holds while the player fills it in (everything optional until saved). */
export interface ProfileDraft {
  displayName: string;
  grade: number | null;
  country: string;
  province: string;
  city: string;
  /** Set when the player picked a known school; null for a typed / new school. */
  schoolId: string | null;
  schoolName: string;
  leaderboardOptIn: boolean;
}

/** Start from the saved profile (edit) or sensible defaults (South Africa, opted out). */
export function draftFromProfile(profile: PlayerProfile | null): ProfileDraft {
  return {
    displayName: profile?.displayName ?? '',
    grade: profile?.grade ?? null,
    country: profile?.country || HOME_COUNTRY,
    province: profile?.province ?? '',
    city: profile?.city ?? '',
    schoolId: profile?.schoolId ?? null,
    schoolName: profile?.schoolName ?? '',
    // Opt-in defaults to off; a returning player keeps their choice.
    leaderboardOptIn: profile?.onboarded ? profile.leaderboardOptIn : false,
  };
}

export function stepIndex(step: OnboardingStepId): number {
  return ONBOARDING_STEPS.indexOf(step);
}

export function nextStep(step: OnboardingStepId): OnboardingStepId | null {
  return ONBOARDING_STEPS[stepIndex(step) + 1] ?? null;
}

export function previousStep(step: OnboardingStepId): OnboardingStepId | null {
  const i = stepIndex(step);
  return i > 0 ? (ONBOARDING_STEPS[i - 1] ?? null) : null;
}

/** Friendly message key for a nickname the filter refused. */
export function nicknameProblemKey(reason: NicknameProblem | undefined): string {
  switch (reason) {
    case 'too-short':
      return 'shell.obNameTooShort';
    case 'too-long':
      return 'shell.obNameTooLong';
    case 'invalid-chars':
      return 'shell.obNameChars';
    case 'no-letters':
      return 'shell.obNameNoLetters';
    case 'too-many-digits':
      return 'shell.obNameDigits';
    case 'not-allowed':
      return 'shell.obNameNotAllowed';
    default:
      return 'shell.obNameInvalid';
  }
}

/** The first problem that keeps the player on `step` (an i18n key), or null when they can go on. */
export function stepProblem(step: OnboardingStepId, draft: ProfileDraft): string | null {
  switch (step) {
    case 'name': {
      const check = isNicknameAllowed(draft.displayName);
      return check.ok ? null : nicknameProblemKey(check.reason);
    }
    case 'grade':
      return draft.grade === null ? 'shell.obNeedGrade' : null;
    case 'school':
      return placeProblem(draft);
    case 'leaderboard':
      return null;
  }
}

function placeProblem(draft: ProfileDraft): string | null {
  if (!/^[A-Za-z]{2}$/.test(draft.country.trim())) return 'shell.obNeedCountry';
  if (provincesFor(draft.country).length > 0 && !findSaProvince(draft.province)) return 'shell.obNeedProvince';
  if (collapseSpaces(draft.city).length === 0) return 'shell.obNeedCity';
  const city = placeTextProblem(draft.city);
  if (city) return placeProblemKey(city);
  if (collapseSpaces(draft.schoolName).length < 2) return 'shell.obNeedSchool';
  const school = placeTextProblem(draft.schoolName);
  if (school) return placeProblemKey(school);
  return null;
}

/** Friendly message key for a school, city or province name that can't be published. */
export function placeProblemKey(reason: PlaceTextProblem): string {
  return reason === 'invalid-chars' ? 'shell.obPlaceChars' : 'shell.obPlaceNotAllowed';
}

/** The first step with a problem (to jump back to), or null when the whole draft is valid. */
export function firstInvalidStep(draft: ProfileDraft): OnboardingStepId | null {
  return ONBOARDING_STEPS.find((s) => stepProblem(s, draft) !== null) ?? null;
}

/**
 * The backend payload for a complete draft (normalised by the same rules the
 * server applies), or null when something is still missing or invalid.
 */
export function draftToInput(draft: ProfileDraft, locale: Locale): ProfileInput | null {
  if (draft.grade === null) return null;
  const checked = validateProfileInput({
    displayName: draft.displayName,
    grade: draft.grade,
    schoolId: draft.schoolId,
    schoolName: draft.schoolName,
    city: draft.city,
    province: draft.province,
    country: draft.country,
    leaderboardOptIn: draft.leaderboardOptIn,
    locale,
  });
  return checked.ok ? checked.value : null;
}

/** Which step a server-side field problem belongs to (to send the player back there). */
export function stepForField(field: ProfileFieldProblem['field']): OnboardingStepId {
  switch (field) {
    case 'displayName':
      return 'name';
    case 'grade':
      return 'grade';
    case 'leaderboardOptIn':
    case 'locale':
      return 'leaderboard';
    default:
      return 'school';
  }
}

/** Message key for a field problem reported by saveProfile (client or server validation). */
export function fieldProblemKey(problem: ProfileFieldProblem): string {
  switch (problem.field) {
    case 'displayName':
      return nicknameProblemKey(asNicknameProblem(problem.reason));
    case 'grade':
      return 'shell.obNeedGrade';
    case 'country':
      return 'shell.obNeedCountry';
    case 'province':
      return placeReasonKey(problem.reason) ?? 'shell.obNeedProvince';
    case 'city':
      return placeReasonKey(problem.reason) ?? 'shell.obNeedCity';
    case 'schoolId':
    case 'schoolName':
      return placeReasonKey(problem.reason) ?? 'shell.obNeedSchool';
    default:
      return 'shell.obSaveFailed';
  }
}

const NICKNAME_PROBLEMS: readonly NicknameProblem[] = [
  'too-short',
  'too-long',
  'invalid-chars',
  'no-letters',
  'too-many-digits',
  'not-allowed',
];

function asNicknameProblem(reason: string): NicknameProblem | undefined {
  return NICKNAME_PROBLEMS.find((p) => p === reason);
}

function placeReasonKey(reason: string): string | null {
  return reason === 'invalid-chars' || reason === 'not-allowed' ? placeProblemKey(reason) : null;
}

/**
 * The sticker a rewarded ad earns next: the first theme (in level order) that
 * the player has already reached in play and hasn't collected yet, or null
 * when every reached theme is collected. Ads never unlock a theme ahead of
 * play: themes are a progression reward, stickers only a cosmetic keepsake.
 */
export function nextStickerId(collected: readonly string[], highestLevelReached: number): string | null {
  return LEVEL_THEMES.slice(0, Math.max(0, highestLevelReached)).find((id) => !collected.includes(id)) ?? null;
}

/**
 * The saved profile as a ProfileInput with some fields changed (Settings →
 * leaderboard opt-in), or null before onboarding (there is nothing to save yet).
 */
export function profileInputFrom(profile: PlayerProfile, patch: Partial<ProfileInput> = {}): ProfileInput | null {
  if (!profile.onboarded || profile.grade === null) return null;
  return {
    displayName: profile.displayName,
    grade: profile.grade,
    schoolId: profile.schoolId,
    schoolName: profile.schoolName,
    city: profile.city,
    province: profile.province,
    country: profile.country,
    leaderboardOptIn: profile.leaderboardOptIn,
    locale: profile.locale,
    ...patch,
  };
}
